import {
	getUserEmailChangeEligibility,
	updateUserEmail,
} from '@dealstack/frontend/shared/dataExchange/services/god.service';
import { getBackendErrorMessage } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import {
	describeEligibility,
	ELIGIBILITY_UNAVAILABLE_EXPLANATION,
	shouldRetryEligibility,
	toEligibilityTarget,
} from '@dealstack/frontend/admin/pages/Manage/User/ManageUser/EmailForm/useEmailFormController.utils';
import { useDSToast } from '@dealstack/frontend/shared/mechanism/toast/useDSToast';
import { useDebounceValue } from '@dealstack/frontend/shared/other/react/hooks/useDebounceValue';
import { DataUserTct } from '@dealstack/shared/lib/generated';
import { EmailChangeEligibilityReason } from '@dealstack/shared/lib/common/emailChangeEligibility';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import * as Yup from 'yup';
import type { IUserEmailForm } from '@dealstack/frontend/admin/pages/Manage/User/ManageUser/EmailForm/useEmailFormController.types';

export enum USER_EMAIL_FORM {
	EMAIL = 'email',
}

export const EMAIL_CHANGE_QUEUED_TITLE = 'Email change queued';

export const ELIGIBILITY_DEBOUNCE_MS = 500;

export const useEmailFormController = (refreshData: () => void, user?: DataUserTct) => {
	const toastAPI = useDSToast();
	const commonYup = useCommonYupValidation();

	const updateEmailForm = useForm<IUserEmailForm>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		resolver: yupResolver(
			Yup.object().shape({
				[USER_EMAIL_FORM.EMAIL]: commonYup.email.required,
			})
		),
		defaultValues: {
			[USER_EMAIL_FORM.EMAIL]: user?.email ?? '',
		},
	});

	const updateUserEmailRequest = useMutation({
		mutationFn: updateUserEmail,
		onError: (error) => {
			updateEmailForm.reset({
				[USER_EMAIL_FORM.EMAIL]: user?.email ?? '',
			});
			toastAPI.error({
				message: getBackendErrorMessage(error) ?? 'Could not update the email address.',
			});
		},
	});

	const typedEmail = useWatch({
		control: updateEmailForm.control,
		name: USER_EMAIL_FORM.EMAIL,
	});
	const debouncedEmail = useDebounceValue(typedEmail, ELIGIBILITY_DEBOUNCE_MS);
	const targetEmail = toEligibilityTarget(debouncedEmail, user?.email);

	const eligibilityRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.GODMODE_USER_USERID_EMAIL_CHANGE_ELIGIBILITY_GET, {
				userId: user?.id ?? '',
			}),
			{ targetEmail },
		],
		queryFn: ({ signal }) =>
			getUserEmailChangeEligibility({ userId: user?.id ?? '', targetEmail }, signal),
		enabled: !!user?.id,
		placeholderData: keepPreviousData,
		retry: shouldRetryEligibility,
		meta: { suppressErrorToast: true },
	});

	const eligibility = eligibilityRequest.data?.payload;
	const isEligibilityUnavailable = eligibilityRequest.isError;

	const emailChangeExplanation = useMemo(
		() =>
			isEligibilityUnavailable
				? ELIGIBILITY_UNAVAILABLE_EXPLANATION
				: describeEligibility(eligibility),
		[eligibility, isEligibilityUnavailable]
	);

	// ENT-701: an IdP-managed address is editable by admins (the pipeline moves the
	// membership); the explanation renders as a hint instead of locking the field.
	const isRefused = eligibility
		? !eligibility.allowed &&
			eligibility.reason !== EmailChangeEligibilityReason.DOMAIN_MANAGED_BY_IDP
		: false;
	const isDestinationCheckPending = !!targetEmail && eligibilityRequest.isPlaceholderData;

	useEffect(() => {
		if (!user?.email) {
			return;
		}
		updateEmailForm.reset({
			[USER_EMAIL_FORM.EMAIL]: user.email,
		});
	}, [user?.email]);

	const onEmailUpdateSubmit = useCallback(
		(formValues: IUserEmailForm) => {
			if (!user?.id) {
				return;
			}
			updateUserEmailRequest.mutate(
				{
					userId: user.id,
					body: formValues,
				},
				{
					onSuccess: () => {
						refreshData();
						toastAPI.success({
							title: EMAIL_CHANGE_QUEUED_TITLE,
							message: `${formValues.email} will replace ${user.email ?? 'the current address'} shortly.`,
						});
					},
				}
			);
		},
		[user, updateUserEmailRequest, refreshData, toastAPI]
	);

	return {
		updateEmailForm,
		onEmailUpdateSubmit,
		isEligibilityLoading: !!user?.id && eligibilityRequest.isPending,
		isEmailChangeBlocked: isRefused || isDestinationCheckPending || isEligibilityUnavailable,
		isEmailFieldLocked: (isRefused || isEligibilityUnavailable) && !targetEmail,
		isEligibilityUnavailable,
		emailChangeExplanation,
	};
};
